import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { alertsApi, type AlertQuery } from '@/lib/api/endpoints';
import { refId } from '@/lib/api/refs';
import { useLiveStore } from '@/stores/live-store';
import { queryKeys } from './keys';
import type { Id } from '@/types/api';

export function useAlerts(query?: AlertQuery) {
  return useQuery({
    queryKey: queryKeys.alerts.list(query),
    queryFn: () => alertsApi.list(query),
    placeholderData: (previous) => previous,
  });
}

export function useAlertSummary() {
  return useQuery({
    queryKey: queryKeys.alerts.summary(),
    queryFn: () => alertsApi.summary(),
    staleTime: 20_000,
  });
}

/** Feeds the red marker state — open alerts, keyed by vehicle. */
export function useOpenAlerts() {
  return useQuery({
    queryKey: queryKeys.alerts.list({ status: 'OPEN', limit: 200 }),
    queryFn: () => alertsApi.list({ status: 'OPEN', limit: 200 }),
    staleTime: 20_000,
  });
}

export function useAcknowledgeAlert() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => alertsApi.acknowledge(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.alerts.all });
    },
  });
}

export function useResolveAlert() {
  const client = useQueryClient();
  const clearAlerting = useLiveStore((s) => s.clearAlerting);
  return useMutation({
    mutationFn: ({ id, note }: { id: Id; note?: string }) => alertsApi.resolve(id, note),
    onSuccess: (alert) => {
      const vehicleId = refId(alert.vehicleId);
      if (vehicleId) clearAlerting(vehicleId);
      client.invalidateQueries({ queryKey: queryKeys.alerts.all });
      toast.success('Alert resolved');
    },
  });
}

/** Bulk acknowledge runs sequentially — the endpoint takes one id at a time. */
export function useBulkAcknowledge() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ids: Id[]) => {
      const results = await Promise.allSettled(ids.map((id) => alertsApi.acknowledge(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: ids.length, failed };
    },
    onSuccess: ({ total, failed }) => {
      client.invalidateQueries({ queryKey: queryKeys.alerts.all });
      if (failed === 0) toast.success(`${total} alert${total === 1 ? '' : 's'} acknowledged`);
      else toast.warning(`${total - failed} acknowledged, ${failed} failed`);
    },
  });
}
